import { StyleSheet, Font } from '@react-pdf/renderer';

// ---------------------------------------------------------------------------
// Brand colours
// ---------------------------------------------------------------------------
export const COLORS = {
  teal: '#008D94',
  navy: '#0e223f',
  orange: '#F7941E',
  green: '#a8ce35',
  darkGray: '#333333',
  midGray: '#808285',
  lightGray: '#F2F2F2',
  borderGray: '#CCCCCC',
  white: '#FFFFFF',
  black: '#000000',
};

// ---------------------------------------------------------------------------
// Font registration
// ---------------------------------------------------------------------------
export function registerFonts() {
  // GerTT is injected via the generate script as global.__GERTT_FONT_PATH__
  const gerttSrc =
    (typeof global !== 'undefined' && global.__GERTT_FONT_PATH__) || null;

  if (gerttSrc) {
    Font.register({ family: 'GerTT', src: gerttSrc, fontWeight: 700 });
  }

  // Disable async hyphenation dictionary fetch — prevents PDFViewer from hanging
  // in browser environments where the dictionary CDN is unreachable.
  Font.registerHyphenationCallback((word) => [word]);
}

// ---------------------------------------------------------------------------
// Shared stylesheet
// ---------------------------------------------------------------------------
export const styles = StyleSheet.create({
  // ── Typography ────────────────────────────────────────────────────────────
  h1: {
    fontSize: 30,
    marginBottom: 24,
    marginTop: 16,
    lineHeight: 1.3
  },
  h2: {
    fontSize: 24,
    marginBottom: 16,
    marginTop: 16,
    fontWeight: 700,
  },
  h3: {
    fontSize: 16,
    marginBottom: 12,
    marginTop: 12,
    fontWeight: 700,
  },
  h4: {
    fontSize: 12,
    marginBottom: 8,
    marginTop: 10,
    fontWeight: 700,
  },
  h5: {
    fontSize: 9,
    marginBottom: 4,
    marginTop: 8,
    fontWeight: 700,
  },
  body: {
    fontSize: 12,
    lineHeight: 1.55,
    marginBottom: 8,
    color: COLORS.darkGray,
  },
  small: {
    fontSize: 7.5,
    color: COLORS.midGray,
    lineHeight: 1.5,
  },
  blockquote: {
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: 1.6,
    color: COLORS.navy,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.teal,
    paddingLeft: 10,
    marginVertical: 4,
  },
  attribution: {
    fontSize: 9,
    color: COLORS.midGray,
    marginTop: 4,
    marginBottom: 8,
    marginLeft: 13,
  },

  // ── Lists ─────────────────────────────────────────────────────────────────
  listItem: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 8,
  },
  listBullet: {
    width: 12,
    fontSize: 12,
    color: COLORS.darkGray,
  },
  listText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 1.5,
    color: COLORS.darkGray,
  },
  
  
  // ── Page ─────────────────────────────────────────────────────────────────
  page: {
    paddingTop: 48,
    paddingBottom: 54,
    paddingHorizontal: 48,
    fontFamily: 'Helvetica',
    fontSize: 9,
    lineHeight: 1.5,
    color: COLORS.darkGray,
    backgroundColor: COLORS.white,
  },

  // ── Header / Footer ───────────────────────────────────────────────────────
  header: {
    position: 'absolute',
    top: 18,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.borderGray,
    paddingBottom: 4,
  },
  headerTitle: {
    fontSize: 7,
    color: COLORS.midGray,
    fontFamily: 'Helvetica',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 0.5,
    borderTopColor: COLORS.borderGray,
    paddingTop: 4,
  },
  footerText: {
    fontSize: 7,
    color: COLORS.midGray,
  },

  // ── Cover ─────────────────────────────────────────────────────────────────
  coverPage: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 0,
    backgroundColor: COLORS.navy,
  },
  coverOverlay: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-end',
    padding: 54,
  },
  coverTitle: {
    fontSize: 48,
    color: COLORS.white,
    lineHeight: 1.1,
    marginBottom: 8,
  },
  coverSubtitle: {
    fontSize: 13,
    color: COLORS.teal,
    marginBottom: 4,
  },
  coverMeta: {
    fontSize: 9,
    color: COLORS.white,
    opacity: 0.7,
  },

  // ── Section cover ─────────────────────────────────────────────────────────
  sectionCoverPage: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  sectionCoverOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: 54,
    maxWidth: '60%',
  },
  sectionCoverTitle: {
    fontSize: 36,
    color: COLORS.white,
    lineHeight: 1.1,
    marginBottom: 12,
  },
  sectionCoverSubtitle: {
    fontSize: 16,
    color: COLORS.white,
    opacity: 0.85,
    lineHeight: 1.3,
  },
  sectionCoverBlockquote: {
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: 1.6,
    color: COLORS.white,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.accentColor,
    paddingLeft: 10,
    marginVertical: 12,
  },
  sectionCoverAttribution: {
    fontSize: 10,
    color: COLORS.white,
    opacity: 0.85,
    marginTop: 4,
    marginBottom: 8,
    marginLeft: 13,
  },

  // ── Layout helpers ────────────────────────────────────────────────────────
  section: {
    marginBottom: 14,
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  col2: {
    flex: 1,
    paddingRight: 8,
  },
  spacer: {
    marginTop: 10,
  },

  // ── Images ────────────────────────────────────────────────────────────────
  image: {
    marginVertical: 8,
    objectFit: 'cover',
  },
  imageCaption: {
    fontSize: 10,
    color: COLORS.midGray,
    marginBottom: 6,
    marginTop: 2,
  },

  // ── Stat / data table ─────────────────────────────────────────────────────
  statGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 8,
  },
  statCell: {
    width: '50%',
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderGray,
  },
  statValue: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.navy,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: 8,
    color: COLORS.midGray,
    lineHeight: 1.4,
  },

  // ── Highlight / spotlight box ─────────────────────────────────────────────
  spotlightBox: {
    borderWidth: 1,
    borderColor: COLORS.teal,
    borderRadius: 4,
    padding: 10,
    marginVertical: 8,
    backgroundColor: '#f0fafa',
  },
  spotlightLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: COLORS.teal,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
  },
  spotlightBoxBody: {
    fontSize: 11,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  spotlightBoxBulletList: {
    marginLeft: 12,
  },

  // ── ERN card ──────────────────────────────────────────────────────────────
  ernCard: {
    borderWidth: 1,
    borderColor: COLORS.green,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
    width: '32%',
  },
  ernTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.darkGray,
    marginBottom: 4,
  },
  ernBody: {
    fontSize: 9,
    lineHeight: 1.5,
    color: COLORS.darkGray,
  },

  // ── Footnotes ─────────────────────────────────────────────────────────────
  footnoteSection: {
    borderTopWidth: 0.5,
    borderTopColor: COLORS.borderGray,
    paddingTop: 6,
    marginTop: 10,
  },
  footnoteText: {
    fontSize: 7,
    lineHeight: 1.5,
    color: COLORS.midGray,
    marginBottom: 2,
  },

  // ── Images (extended) ────────────────────────────────────────────────────
  heroImage: {
    width: '100%',
    height: 160,
    objectFit: 'cover',
    marginBottom: 12,
    borderRadius: 3,
    aspectRatio: 16 / 9,
  },
  sectionCoverBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  inlineImage: {
    width: '100%',
    height: 220,
    objectFit: 'cover',
    marginTop: 8,
    borderRadius: 3,
  },
  bigImage: {
    aspectRatio: 3 / 2,
    width: '100%',
    objectFit: 'cover',
    marginVertical: 8,
    borderRadius: 3,
  },
  chartImage: {
    width: '100%',
    marginVertical: 8,
  },
  twoImageRow: {
    flexDirection: 'row',
    gap: 8,
  },
  ernLogo: {
    width: 80,
    height: 32,
    objectFit: 'contain',
    marginBottom: 6,
  },
  badgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginVertical: 10,
    gap: 10,
  },
  badgeImage: {
    height: 72,
    width: 72,
    objectFit: 'contain',
  },
  partnerLogoRow: {
    flexDirection: 'column',
    marginTop: 8,
    gap: 8,
  },
  partnerLogoGridRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  partnerLogo: {
    flex: 1,
    objectFit: 'contain',
    height: 36,
  },
  // Per-logo overrides — adjust height/flex to balance visual weight
  partnerLogo_afirm:           { flex: 1, objectFit: 'contain', height: 60 },
  partnerLogo_aafa:            { flex: 1, objectFit: 'contain', height: 40 },
  partnerLogo_betterWork:      { flex: 1, objectFit: 'contain', height: 48 },
  partnerLogo_corp:            { flex: 1, objectFit: 'contain', height: 60 },
  partnerLogo_canopy:          { flex: 1, objectFit: 'contain', height: 28 },
  partnerLogo_cascale:         { flex: 1, objectFit: 'contain', height: 92 },
  partnerLogo_fesi:            { flex: 1, objectFit: 'contain', height: 52 },
  partnerLogo_lwg:             { flex: 1, objectFit: 'contain', height: 52 },
  partnerLogo_oia:             { flex: 1, objectFit: 'contain', height: 80 },
  partnerLogo_rila:            { flex: 1, objectFit: 'contain', height: 100 },
  partnerLogo_rise:            { flex: 1, objectFit: 'contain', height: 36 },
  partnerLogo_textileExchange: { flex: 1, objectFit: 'contain', height: 32 },
  partnerLogo_wfsgi:           { flex: 1, objectFit: 'contain', height: 44 },

   // ── Pill Headings ────────────────────────────────────────────────────
    pillHeading: {
      fontSize: 12,
      color: COLORS.white,
      backgroundColor: COLORS.green,
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 12,
      display: 'inline-block',
      textTransform: 'uppercase',
      alignSelf: 'flex-start',
    },
});
